import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useCreateTodoList } from '@/hooks';
import { brandColors } from '@/theme/colors';

type CreateTodoListDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
};

export const CreateTodoListDialog = ({
  open,
  onClose,
  onSuccess,
}: CreateTodoListDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const createMutation = useCreateTodoList();

  const handleCreate = async () => {
    setError('');

    if (!name.trim()) {
      setError('Please enter a list name');
      return;
    }

    try {
      const id = await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim(),
      });
      setName('');
      setDescription('');
      onClose();
      onSuccess?.(id);
    } catch (err) {
      setError('Failed to create ToDo list');
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && name.trim()) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: brandColors.purple }}>Create ToDo List</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="List Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            fullWidth
            required
            placeholder="e.g., Shopping List, Project Tasks..."
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            placeholder="Add a description for your list..."
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={createMutation.isPending || !name.trim()}
          startIcon={<Add />}
          sx={{
            bgcolor: brandColors.purple,
            '&:hover': { bgcolor: brandColors.purpleDeep },
          }}
        >
          Create List
        </Button>
      </DialogActions>
    </Dialog>
  );
};
